/**
 * Pending CBO Applications Component
 * Displays pending CBO applications for sponsor review and approval
 */

import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, AlertCircle, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { sponsorApplicationsAPI, type CBOApplication } from '@/services/api/sponsorApplications';
import { ApplicationReviewModal } from './ApplicationReviewModal';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface PendingApplicationsProps {
  sponsorId: number;
  programId?: number;
  onApplicationUpdate?: () => void;
}

export const PendingApplications: React.FC<PendingApplicationsProps> = ({
  sponsorId,
  programId,
  onApplicationUpdate
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [applications, setApplications] = useState<CBOApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<CBOApplication | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [sponsorId, programId]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sponsorApplicationsAPI.getPendingApplications(sponsorId, programId);
      setApplications(data);
    } catch (err: any) {
      console.error('Failed to fetch applications:', err);
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (application: CBOApplication) => {
    setSelectedApplication(application);
    setShowReviewModal(true);
  };

  const handleModalClose = () => {
    setShowReviewModal(false);
    setSelectedApplication(null);
  };

  const handleApplicationAction = async () => {
    await fetchApplications();
    if (onApplicationUpdate) {
      onApplicationUpdate();
    }
    handleModalClose();
  };

  const getMatchQualityBadge = (score: number) => {
    if (score >= 90) return <Badge className="bg-green-100 text-green-800">{t('sponsors.excellent', 'Excellent')}</Badge>;
    if (score >= 80) return <Badge className="bg-blue-100 text-blue-800">{t('sponsors.good', 'Good')}</Badge>;
    if (score >= 70) return <Badge className="bg-yellow-100 text-yellow-800">{t('sponsors.fair', 'Fair')}</Badge>;
    return <Badge className="bg-gray-100 text-gray-800">{t('sponsors.poor', 'Poor')}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('sponsors.loading_applications', 'Loading applications...')}</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <Button onClick={fetchApplications} className="mt-4">
            {t('sponsors.retry', 'Retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (applications.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium">{t('sponsors.no_pending_applications', 'No Pending Applications')}</p>
          <p className="text-gray-500 mt-2">{t('sponsors.all_applications_have_been_reviewed', 'All applications have been reviewed')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">{t('sponsors.pending_applications', 'Pending Applications')}</h3>
            <p className="text-sm text-gray-600">
              {applications.length} application{applications.length !== 1 ? 's' : ''} awaiting review
            </p>
          </div>
        </div>

        <div className="grid gap-4">
          {applications.map((application) => (
            <Card key={application.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold">{application.cbo_name}</h4>
                      {getMatchQualityBadge(application.match_score_at_application)}
                      <span className="text-sm text-gray-500">
                        Match Score: {application.match_score_at_application.toFixed(1)}%
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mb-3">
                      Program: {application.program_name}
                    </p>

                    {application.application_message && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {application.application_message}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      {application.cbo_quality_score && (
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('sponsors.quality_score', 'Quality Score')}</p>
                            <p className="text-sm font-semibold">
                              {application.cbo_quality_score.toFixed(1)}/10
                            </p>
                          </div>
                        </div>
                      )}

                      {application.cbo_current_members !== undefined && (
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('sponsors.capacity', 'Capacity')}</p>
                            <p className="text-sm font-semibold">
                              {application.cbo_current_members}/{application.cbo_capacity}
                            </p>
                          </div>
                        </div>
                      )}

                      {application.predicted_enrollments && (
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('sponsors.predictedyear', 'Predicted/Year')}</p>
                            <p className="text-sm font-semibold">
                              {application.predicted_enrollments}
                            </p>
                          </div>
                        </div>
                      )}

                      {application.predicted_roi && (
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('sponsors.est_roi', 'Est. ROI')}</p>
                            <p className="text-sm font-semibold">
                              {application.predicted_roi.toFixed(1)}x
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-gray-500">
                      Applied: {fmt.short(application.application_date)}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      onClick={() => handleReviewClick(application)}
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {t('sponsors.review', 'Review')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {showReviewModal && selectedApplication && (
        <ApplicationReviewModal
          application={selectedApplication}
          onClose={handleModalClose}
          onApprove={handleApplicationAction}
          onReject={handleApplicationAction}
        />
      )}
    </>
  );
};
